import {
  Box,
  CircularProgress as MuiCircularProgress,
  Typography,
} from '@mui/material';
import { FC } from 'react';

interface CircularProgressProps {
  progress: number;
  progressLabel?: number;
  label?: string;
  thickness?: number;
  size?: number;
}

export const CircularProgress: FC<CircularProgressProps> = ({
  progress,
  progressLabel,
  label,
  thickness = 6,
  size = 250,
}) => {
  const normalizedProgress = Math.floor(progress);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <svg style={{ position: 'absolute', width: 0, height: 0 }}>
          <defs>
            <linearGradient id="retention-gradient">
              <stop offset="0%" stopColor="#134085" />
              <stop offset="100%" stopColor="#42a5f5" />
            </linearGradient>
          </defs>
        </svg>
        <MuiCircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={thickness}
          aria-label="Determinate circular progress bar used for visual effect"
          sx={{
            color: 'grey.200',
          }}
        />
        <MuiCircularProgress
          variant="determinate"
          value={normalizedProgress}
          size={size}
          thickness={thickness}
          aria-label={`Progress bar set to ${normalizedProgress} of 100`}
          sx={{
            position: 'absolute',
            left: 0,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
              animation: 'progress 2s ease-out forwards',
              stroke: 'url(#retention-gradient)',
            },
            '@keyframes progress': {
              '0%': {
                strokeDasharray: '0 100',
              },
              '100%': {
                strokeDasharray: `${normalizedProgress * 1.2} 100`,
              },
            },
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h3"
            component="div"
            sx={{
              color: 'text.secondary',
            }}
          >
            {progressLabel}%
          </Typography>
        </Box>
      </Box>
      {label && (
        <Typography
          variant="h4"
          sx={{
            textTransform: 'uppercase',
            fontWeight: 800,
            mt: 4,
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
};
